import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import type { AccesoTemporal as Acceso } from '../lib/tipos'

/**
 * El supervisor abre un contrato cerrado a alguien durante un rato.
 * Tal como se pidió: los contratos activos no los ve nadie más, y si hace falta
 * que un comercial mire el suyo, se le da acceso y caduca solo.
 */
export function AccesoTemporal({ uuid }: { uuid: string }) {
  const clienteQuery = useQueryClient()
  const [usuarioId, setUsuarioId] = useState('')
  const [horas, setHoras] = useState('24')
  const [motivo, setMotivo] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)

  const usuarios = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api<{ usuarios: Array<{ id: number; nombre: string; rol: string }> }>('/api/usuarios'),
  })

  const accesos = useQuery({
    queryKey: ['accesos', uuid],
    queryFn: () => api<{ accesos: Acceso[] }>(`/api/fichas/${uuid}/accesos`),
  })

  const conceder = useMutation({
    mutationFn: () =>
      api<{ accesos: Acceso[] }>(`/api/fichas/${uuid}/acceso`, {
        metodo: 'POST',
        cuerpo: { usuario_id: Number(usuarioId), horas: Number(horas), motivo },
      }),
    onSuccess: (datos) => {
      setAviso(null)
      setMotivo('')
      clienteQuery.setQueryData(['accesos', uuid], datos)
    },
    onError: (e) => setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido'),
  })

  const revocar = useMutation({
    mutationFn: (id: number) =>
      api<{ accesos: Acceso[] }>(`/api/fichas/${uuid}/acceso/revocar`, {
        metodo: 'POST',
        cuerpo: { usuario_id: id },
      }),
    onSuccess: (datos) => clienteQuery.setQueryData(['accesos', uuid], datos),
  })

  const entrada =
    'w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950'

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
        Acceso temporal
      </h3>

      {accesos.data?.accesos.length ? (
        <ul className="mb-3 space-y-1 text-xs">
          {accesos.data.accesos.map((acceso) => (
            <li key={acceso.usuario_id} className="flex items-center gap-2">
              <span className="font-medium">{acceso.nombre}</span>
              <span className="text-slate-400">hasta {acceso.expira_en}</span>
              <button
                onClick={() => revocar.mutate(acceso.usuario_id)}
                className="ml-auto text-slate-500 hover:text-red-600 hover:underline"
              >
                quitar
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-3 text-xs text-slate-400">Nadie más puede ver este contrato.</p>
      )}

      <div className="space-y-2">
        <select value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} className={entrada}>
          <option value="">— a quién —</option>
          {usuarios.data?.usuarios.map((usuario) => (
            <option key={usuario.id} value={usuario.id}>
              {usuario.nombre} ({usuario.rol})
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <input
            value={horas}
            onChange={(e) => setHoras(e.target.value)}
            type="number"
            min="1"
            max="72"
            className={`${entrada} w-20`}
            title="Horas"
          />
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo"
            className={entrada}
          />
        </div>
        <button
          onClick={() => conceder.mutate()}
          disabled={!usuarioId || conceder.isPending}
          className="w-full rounded-lg bg-marca-700 px-3 py-2 text-sm font-bold text-white transition hover:bg-marca-800 disabled:opacity-40"
        >
          Dar acceso
        </button>
        {aviso && <p className="text-xs text-red-600">{aviso}</p>}
      </div>
    </div>
  )
}
